/** OTA spreadsheet import — a 2-step wizard dialog.
 *
 *  Step 1: provide the sheet (upload a CSV/XLSX, or a Google Sheets URL) →
 *          `importPreview` parses + caches it and returns columns, sample rows,
 *          an autodetected format+mapping, and the catalog of selectable formats.
 *  Step 2: pick a format and map each of its logical fields to a spreadsheet
 *          column; the preview table highlights mapped columns. "Dry run" and
 *          "Start import" call `importStart({ cache_key, format, mapping, dry_run })`.
 *
 *  On the 201 response the import runs as a background task — we show a snackbar
 *  pointing the user to Background Tasks (the existing bell polls it) and reset. */
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useRef, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as otasApi from '@/api/otas';
import type { ImportPreviewResult } from '@/api/otas';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';

const SHEET_URL_RE = /^https:\/\/docs\.google\.com\/spreadsheets\/d\/(e\/)?[A-Za-z0-9_-]+/;
const FILE_ACCEPT = '.csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export function OtaImportWizard({ open, onClose }: { open: boolean; onClose: () => void }) {
  const snackbar = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [activeStep, setActiveStep] = useState(0);
  const [inputTab, setInputTab] = useState<'file' | 'url'>('file');
  const [file, setFile] = useState<File | null>(null);
  const [sheetUrl, setSheetUrl] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<ImportPreviewResult | null>(null);
  const [format, setFormat] = useState('');
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setActiveStep(0);
    setInputTab('file');
    setFile(null);
    setSheetUrl('');
    setPreviewing(false);
    setPreview(null);
    setFormat('');
    setMapping({});
    setSubmitting(false);
  };

  const closeAndReset = () => {
    reset();
    onClose();
  };

  const canPreview = inputTab === 'file' ? !!file : SHEET_URL_RE.test(sheetUrl.trim());

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const data =
        inputTab === 'file'
          ? await otasApi.importPreview({ file: file! })
          : await otasApi.importPreview({ sheetUrl: sheetUrl.trim() });
      setPreview(data);
      const autoFormat = data.autodetect?.format ?? '';
      setFormat(autoFormat);
      // Seed mapping from autodetect, dropping any null column suggestions.
      const auto = data.autodetect?.mapping ?? {};
      setMapping(
        Object.fromEntries(
          Object.entries(auto).filter((e): e is [string, string] => Boolean(e[1])),
        ),
      );
      setActiveStep(1);
    } catch (e) {
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Preview failed.', 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const formatDescriptor = preview && format ? preview.formats[format] : undefined;

  const requiredMissing = useMemo(
    () =>
      (formatDescriptor?.fields ?? [])
        .filter((f) => f.required && !mapping[f.key])
        .map((f) => f.label),
    [formatDescriptor, mapping],
  );

  const mappedColumns = useMemo(
    () => new Set(Object.values(mapping).filter(Boolean)),
    [mapping],
  );

  const submitImport = async (dryRun: boolean) => {
    if (!preview || !format) return;
    setSubmitting(true);
    try {
      const res = await otasApi.importStart({
        cache_key: preview.cache_key,
        format,
        mapping,
        dry_run: dryRun,
      });
      snackbar.show(
        `${dryRun ? 'OTA import dry run' : 'OTA import'} started (task ${res.id}) — track it in Background Tasks.`,
        'success',
      );
      closeAndReset();
    } catch (e) {
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Import failed to start.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMappingChange = (fieldKey: string, column: string) =>
    setMapping((prev) => {
      const next = { ...prev };
      if (column) next[fieldKey] = column;
      else delete next[fieldKey];
      return next;
    });

  return (
    <AppDialog open={open} onClose={closeAndReset} size="lg" title="Import OTAs" disableBackdropClose>
      <Stepper activeStep={activeStep} orientation="vertical">
        <Step>
          <StepLabel>Provide the spreadsheet</StepLabel>
          <StepContent>
            <Tabs
              value={inputTab}
              onChange={(_, v) => setInputTab(v as 'file' | 'url')}
              sx={{ mb: 2 }}
            >
              <Tab value="file" label="Upload file" />
              <Tab value="url" label="Google Sheets URL" />
            </Tabs>

            {inputTab === 'file' ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={FILE_ACCEPT}
                  style={{ display: 'none' }}
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Choose CSV or XLSX
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {file ? file.name : 'No file selected.'}
                </Typography>
              </Box>
            ) : (
              <TextField
                fullWidth
                size="small"
                label="Google Sheets URL"
                placeholder="https://docs.google.com/spreadsheets/d/..."
                value={sheetUrl}
                onChange={(e) => setSheetUrl(e.target.value)}
                helperText="Set sharing to 'Anyone with the link' (Viewer), or use File → Share → Publish to web (CSV)."
              />
            )}

            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                disabled={!canPreview || previewing}
                onClick={handlePreview}
                startIcon={previewing ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                Next: preview and map
              </Button>
            </Box>
          </StepContent>
        </Step>

        <Step>
          <StepLabel>Choose columns and import</StepLabel>
          <StepContent>
            {preview && (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Source: <strong>{preview.source?.name ?? 'spreadsheet'}</strong> — {preview.row_count}{' '}
                  row{preview.row_count === 1 ? '' : 's'}.
                </Alert>

                <TextField
                  select
                  fullWidth
                  size="small"
                  label="CSV format"
                  value={format}
                  onChange={(e) => {
                    setFormat(e.target.value);
                    setMapping({});
                  }}
                  sx={{ mb: 2, maxWidth: 360 }}
                >
                  {Object.entries(preview.formats).map(([key, d]) => (
                    <MenuItem key={key} value={key}>
                      {d.label}
                    </MenuItem>
                  ))}
                </TextField>

                {formatDescriptor && (
                  <>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 1.5,
                        mb: 2,
                      }}
                    >
                      {formatDescriptor.fields.map((f) => (
                        <TextField
                          key={f.key}
                          select
                          size="small"
                          label={f.required ? `${f.label} *` : f.label}
                          value={mapping[f.key] ?? ''}
                          onChange={(e) => handleMappingChange(f.key, e.target.value)}
                          error={f.required && !mapping[f.key]}
                        >
                          <MenuItem value="">
                            <em>{f.required ? '— select —' : 'Not mapped'}</em>
                          </MenuItem>
                          {preview.columns.map((col) => (
                            <MenuItem key={col} value={col}>
                              {col}
                            </MenuItem>
                          ))}
                        </TextField>
                      ))}
                    </Box>

                    {requiredMissing.length > 0 && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        Map the following required field(s): {requiredMissing.join(', ')}.
                      </Alert>
                    )}
                  </>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Preview (first {preview.preview.length} row{preview.preview.length === 1 ? '' : 's'})
                </Typography>
                <TableContainer sx={{ maxHeight: 360, mb: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {preview.columns.map((col) => (
                          <TableCell
                            key={col}
                            sx={{ fontWeight: 600, bgcolor: mappedColumns.has(col) ? 'success.light' : undefined }}
                          >
                            {col}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.preview.map((row, ri) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <TableRow key={ri}>
                          {preview.columns.map((col, ci) => (
                            <TableCell
                              // eslint-disable-next-line react/no-array-index-key
                              key={ci}
                              sx={{ bgcolor: mappedColumns.has(col) ? 'success.light' : undefined }}
                            >
                              {row[ci]}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Button onClick={() => setActiveStep(0)} disabled={submitting}>
                    Back
                  </Button>
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    variant="outlined"
                    disabled={submitting || requiredMissing.length > 0 || !format}
                    onClick={() => void submitImport(true)}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
                  >
                    Dry run
                  </Button>
                  <Button
                    variant="contained"
                    disabled={submitting || requiredMissing.length > 0 || !format}
                    onClick={() => void submitImport(false)}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
                  >
                    Start import
                  </Button>
                </Box>
              </>
            )}
          </StepContent>
        </Step>
      </Stepper>
    </AppDialog>
  );
}
